import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Checkbox from '@mui/material/Checkbox';
import Button from '../BaseComponents/Button';
import Loader from '../BaseComponents/Loader';
import { downloadOnboardingFormPdf, getMemberMedicalCard } from '../../api/members';
import { RootState } from '../../store/store';
import { setMemberMedicalCard } from '../../store/memberSlice';
import {
  buildOnboardingFormSections,
  getOnboardingDisclaimerItemsForMember,
  isOnboardingDisclaimerItemConfirmed,
  isOnboardingFormCompleted
} from '../../utils/onboardingFormDisplay';
import '../ScanQuestionnaires/ScanQuestionnaires.scss';

type QuestionnaireRow = {
  label: string;
  value: string;
};

const QuestionnaireTable: React.FC<{
  columnLabels: [string, string];
  rows: QuestionnaireRow[];
}> = ({ columnLabels, rows }) => (
  <div className="questionnaire-table">
    <div className="questionnaire-row questionnaire-head">
      <div>{columnLabels[0]}</div>
      <div>{columnLabels[1]}</div>
    </div>
    {rows.map((row) => (
      <div key={row.label} className="questionnaire-row">
        <div>{row.label}</div>
        <div className="questionnaire-answer">{row.value}</div>
      </div>
    ))}
  </div>
);

const OnboardingFormToolbar: React.FC<{
  showDownload?: boolean;
  isDownloading?: boolean;
  onDownload?: () => void;
}> = ({ showDownload, isDownloading, onDownload }) => (
  <div className="scan-questionnaires-toolbar sideBarPad2">
    <div className="scan-questionnaires-count">Onboarding form</div>
    {showDownload && onDownload && (
      <div className="scan-questionnaires-toolbar-actions">
        <Button
          text="Download form"
          onClick={onDownload}
          isSecondary={true}
          variant="download"
          isLoading={isDownloading}
          isDisabled={isDownloading}
        />
      </div>
    )}
  </div>
);

const EmptyTable: React.FC<{ message: string }> = ({ message }) => (
  <div className="scan-questionnaires-table">
    <div className="scan-questionnaires-table-header">
      <div className="header-cell">Question</div>
      <div className="header-cell">Answer</div>
    </div>
    <div className="scan-questionnaires-table-body">
      <div className="empty-state">
        <div className="empty-text">{message}</div>
      </div>
    </div>
  </div>
);

const OnboardingFormData: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const memberId = location.pathname.split('/')[2];
  const { memberData, medicalCard } = useSelector((state: RootState) => state.member);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(true);

  const isCompleted = isOnboardingFormCompleted(medicalCard);
  const scanInfo = medicalCard?.scan_info;

  useEffect(() => {
    if (!memberId) {
      setIsRefreshing(false);
      return;
    }

    let cancelled = false;

    const refreshMedicalCard = async () => {
      try {
        const card = await getMemberMedicalCard(memberId);
        if (!cancelled) {
          dispatch(setMemberMedicalCard(card));
        }
      } catch {
        // Keep existing Redux data if refresh fails.
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
        }
      }
    };

    refreshMedicalCard();

    return () => {
      cancelled = true;
    };
  }, [memberId, dispatch]);

  const handleDownload = async () => {
    if (!memberId) return;
    setIsDownloading(true);
    try {
      const memberName = `${memberData?.first_name || ''} ${memberData?.last_name || ''}`.trim();
      await downloadOnboardingFormPdf(memberId, {
        memberName,
        submittedAt: scanInfo?.onboarding_form_completed_at,
      });
      toast.success('Onboarding form downloaded successfully');
    } catch (error: any) {
      const message =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to download onboarding form';
      toast.error(message);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!memberData || isRefreshing) {
    return (
      <div className="scan-questionnaires-container">
        <Loader />
      </div>
    );
  }

  if (!isCompleted) {
    return (
      <div className="scan-questionnaires-container">
        <OnboardingFormToolbar />
        <EmptyTable message="No onboarding form has been submitted yet." />
      </div>
    );
  }

  const sections = buildOnboardingFormSections(medicalCard, memberData);

  return (
    <div className="scan-questionnaires-container">
      <OnboardingFormToolbar
        showDownload
        isDownloading={isDownloading}
        onDownload={handleDownload}
      />

      <div className="scan-questionnaires-content">
        <div className="questionnaire-sections">
          {sections.map((section) => (
            <div key={section.title} className="questionnaire-section">
              <div className="questionnaire-section-header">
                <h3>{section.title}</h3>
              </div>
              <QuestionnaireTable
                columnLabels={['Question', 'Answer']}
                rows={section.rows}
              />
            </div>
          ))}

          <div className="questionnaire-section">
            <div className="questionnaire-section-header">
              <h3>Declaration</h3>
            </div>
            <div className="questionnaire-disclaimer-checkboxes">
              {getOnboardingDisclaimerItemsForMember(
                memberData.gender,
                memberData.membership_type
              ).map((item, index) => (
                <div key={item} className="questionnaire-disclaimer-checkbox">
                  <Checkbox
                    id={`onboarding-disclaimer-${index}`}
                    checked={isOnboardingDisclaimerItemConfirmed(scanInfo, item)}
                    disabled
                    size="small"
                  />
                  <label htmlFor={`onboarding-disclaimer-${index}`}>{item}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFormData;
